<template>
    <div class="inv-page">

        <!-- Hidden PDF render layer -->
        <div v-if="preparingPDFInventory" style="padding: 10px; background: white;">
            <div v-for="(room, rIndex) in roomPDFPagination" :key="'room-' + rIndex">
                <div
                    v-for="(block, bIndex) in room.furnitureBlocks"
                    :key="'block-' + bIndex"
                    ref="pdfPage"
                    class="pdf-page"
                    style="width: 794px; background: white; padding: 8px; margin-bottom: 5px; box-sizing: border-box;"
                >
                    <!-- Room name: only on first item of the room -->
                    <h3 v-if="block.isFirstInRoom" style="margin: 0 0 0 0; font-size: 13px; font-weight: bold; padding: 5px 10px; background: #e0e0e0; border-left: 4px solid #555; color: #222;">
                        {{ room.name }}
                    </h3>

                    <!-- One table per furniture item — furniture row + defect row in the same table -->
                    <table style="width: 100%; border-collapse: collapse; table-layout: fixed; font-size: 12px;">
                        <thead>
                            <tr style="background: #e8e8e8; color: #333; text-align: center;">
                                <th style="padding: 6px; border: 1px solid #ccc; width: 20%;">Detail</th>
                                <th style="padding: 6px; border: 1px solid #ccc; width: 40%;">Images</th>
                                <th style="padding: 6px; border: 1px solid #ccc; width: 25%;">Description</th>
                                <th style="padding: 6px; border: 1px solid #ccc; width: 15%;"></th>
                            </tr>
                        </thead>
                        <tbody>
                            <!-- Main row: item name + images + description + checkboxes -->
                            <tr style="background: #ffffff; vertical-align: top;">
                                <!-- Detail: comma-separated names each on its own line -->
                                <td v-if="block.showItemName" style="padding: 5px; border: 1px solid #ccc; word-break: break-word;" v-html="block.item.name.split(',').map(n => n.trim()).join('<br>')"></td>
                                <td v-else style="padding: 5px; border: 1px solid #ccc; font-size: 11px; color: #666; font-style: italic;" v-html="block.item.name.split(',').map(n => n.trim()).join('<br>') + '<br>(cont.)'"></td>

                                <td style="padding: 5px; border: 1px solid #ccc;">
                                    <div v-if="block.furnitureImages && block.furnitureImages.length">
                                        <div style="font-weight: bold; font-size: 11px; margin-bottom: 3px; color: #333;">Furniture Images</div>
                                        <div style="display: flex; flex-wrap: wrap; gap: 3px;">
                                            <div
                                                v-for="(img, i) in block.furnitureImages"
                                                :key="'f-'+i"
                                                :style="{ width: img.landscape ? '100%' : '48%', height: '130px', overflow: 'hidden', border: '1px solid #ccc' }"
                                            >
                                                <img :src="img.url" style="width: 100%; height: 100%; object-fit: cover;" @load="checkImageOrientation(img, $event)" />
                                            </div>
                                        </div>
                                    </div>
                                    <div v-if="!block.furnitureImages.length && !block.defectImages.length" style="color: #aaa; font-size: 11px;">No images</div>
                                </td>

                                <td v-if="block.showItemName" style="padding: 5px; border: 1px solid #ccc; white-space: pre-wrap; word-wrap: break-word; color: #333;">{{ block.item.description || '' }}</td>
                                <td v-else style="padding: 5px; border: 1px solid #ccc;"></td>

                                <td v-if="block.showItemName" style="padding: 5px; border: 1px solid #ccc; vertical-align: top;">
                                    <div class="form-check" style="margin: 3px 0;">
                                        <input type="checkbox" class="form-check-input" :id="'arr-'+rIndex+'-'+bIndex" v-model="block.item.arrivalChecked" />
                                        <label class="form-check-label" :for="'arr-'+rIndex+'-'+bIndex">Check-in</label>
                                    </div>
                                    <div class="form-check" style="margin: 3px 0;">
                                        <input type="checkbox" class="form-check-input" :id="'dep-'+rIndex+'-'+bIndex" v-model="block.item.departureChecked" />
                                        <label class="form-check-label" :for="'dep-'+rIndex+'-'+bIndex">Check-out</label>
                                    </div>
                                </td>
                                <td v-else style="padding: 5px; border: 1px solid #ccc;"></td>
                            </tr>

                            <!-- Defect row: same table as the last furniture chunk for this item -->
                            <tr v-if="block.defectImages && block.defectImages.length" style="background: #ffffff; vertical-align: top;">
                                <td style="padding: 5px; border: 1px solid #ccc;"></td>
                                <td style="padding: 5px; border: 1px solid #ccc;">
                                    <div style="font-weight: bold; font-size: 11px; margin-bottom: 3px; color: #333;">Defect Images</div>
                                    <div style="display: flex; flex-wrap: wrap; gap: 3px;">
                                        <div
                                            v-for="(img, i) in block.defectImages"
                                            :key="'d-'+i"
                                            :style="{ width: img.landscape ? '100%' : '48%', height: '130px', overflow: 'hidden', border: '1px solid #ccc' }"
                                        >
                                            <img :src="img.url" style="width: 100%; height: 100%; object-fit: cover;" @load="checkImageOrientation(img, $event)" />
                                        </div>
                                    </div>
                                </td>
                                <td style="padding: 5px; border: 1px solid #ccc;"></td>
                                <td style="padding: 5px; border: 1px solid #ccc;"></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>

        <!-- ── Main UI ── -->
        <div class="inv-wrapper">

            <!-- Top Action Bar -->
            <div class="inv-topbar">
                <div class="inv-topbar-title">Inventory Management</div>
                <div class="inv-topbar-actions">
                    <router-link v-if="propertyId" class="inv-btn inv-btn-outline" :to="{ name: 'property-edit', params: { id: propertyId } }">
                        &#8592; Back to Property
                    </router-link>
                    <router-link class="inv-btn inv-btn-outline" :to="{ name: 'keyhand-add', params: { id: propertyId } }">
                        &#128273; Key Handover
                    </router-link>
                    <!-- Download dropdown -->
                    <div class="inv-dropdown-wrap" v-click-outside="closeDownloadMenu">
                        <button class="inv-btn inv-btn-download-all" @click="toggleDownloadMenu" :disabled="downloadingAll">
                            <span v-if="downloadingAll">&#9203; Preparing...</span>
                            <span v-else>&#11015; Download &#9660;</span>
                        </button>
                        <div v-if="showDownloadMenu" class="inv-dropdown-menu">
                            <button class="inv-dropdown-item" @click="downloadOne('keyhand')">
                                &#128273; Key Handover
                            </button>
                            <button class="inv-dropdown-item" @click="downloadOne('footer')">
                                &#128203; Footer PDF
                            </button>
                            <button class="inv-dropdown-item" @click="downloadOne('furniture')">
                                &#128196; Furniture List
                            </button>
                            <div class="inv-dropdown-divider"></div>
                            <button class="inv-dropdown-item inv-dropdown-item-all" @click="downloadOne('all')">
                                &#11015; Download All
                            </button>
                        </div>
                    </div>
                    <button class="inv-btn inv-btn-preview" @click="openPreview">
                        &#128065; Preview
                    </button>
                    <button class="inv-btn inv-btn-primary" @click="addRoom">
                        + Add Room
                    </button>
                </div>
            </div>

            <!-- ── Preview Modal ── -->
            <div v-if="showPreview" class="preview-overlay" @click.self="showPreview = false">
                <div class="preview-modal">
                    <div class="preview-modal-header">
                        <div class="preview-tabs">
                            <button class="preview-tab" :class="{ 'preview-tab-active': previewTab === 'keyhand' }" @click="previewTab = 'keyhand'">&#128273; Key Handover</button>
                            <button class="preview-tab" :class="{ 'preview-tab-active': previewTab === 'furniture' }" @click="previewTab = 'furniture'">&#128196; Furniture List</button>
                            <button class="preview-tab" :class="{ 'preview-tab-active': previewTab === 'footer' }" @click="previewTab = 'footer'">&#128203; Footer</button>
                        </div>
                        <button class="preview-close-btn" @click="showPreview = false">&#215;</button>
                    </div>
                    <div class="preview-modal-body">

                        <!-- ── Tab: Key Handover ── -->
                        <div v-if="previewTab === 'keyhand'" class="prev-paper">
                            <h3 class="prev-kh-title">Key Handover</h3>

                            <table style="width: 100%; border-collapse: collapse; font-size: 12px; color: #212529; margin-bottom: 12px;">
                                <tr>
                                    <td style="padding: 4px 0; width: 50%;"><strong>Unit No:</strong> {{ propertyData && propertyData.address_no ? propertyData.address_no : '' }}</td>
                                    <td style="padding: 4px 0;"><strong>Date of Record:</strong> {{ form && form.date ? formatDate(form.date) : '' }}</td>
                                </tr>
                                <tr>
                                    <td style="padding: 4px 0;"><strong>Residence:</strong> {{ propertyData && propertyData.place_name ? propertyData.place_name[currentLocale] : '' }}</td>
                                    <td style="padding: 4px 0;"><strong>Building/Floor:</strong> {{ propertyData ? ((propertyData.buildiing ? 'Building ' + propertyData.buildiing : '') + ' ' + (propertyData.floors || '')).trim() : '' }}</td>
                                </tr>
                            </table>

                            <p style="font-size: 12px; color: #212529; margin: 0 0 12px;">The unit owner hereby acknowledges receipt of the following keys to the premises</p>

                            <h5 class="prev-kh-section">Handover Items</h5>
                            <table class="prev-kh-table" style="text-align: center;">
                                <thead>
                                    <tr>
                                        <th>No</th>
                                        <th>Item/Location</th>
                                        <th>Key No.</th>
                                        <th>Total</th>
                                        <th>Photo</th>
                                        <th>Check-in</th>
                                        <th>Check-out</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(item, index) in (form && form.handoverItems || [])" :key="'kh-item-'+index">
                                        <td>{{ index + 1 }}</td>
                                        <td>{{ item.location }}</td>
                                        <td>{{ item.number }}</td>
                                        <td>{{ item.total }}</td>
                                        <td>
                                            <img v-if="item.image" :src="item.image" style="width: 90px; height: 90px; object-fit: cover; border-radius: 4px; border: 1px solid #ddd; cursor: pointer;" @click="openImagePreview(item.image)" />
                                        </td>
                                        <td></td>
                                        <td></td>
                                    </tr>
                                    <tr v-if="!form || !form.handoverItems || !form.handoverItems.length">
                                        <td colspan="7" style="text-align: center; color: #9ca3af; padding: 10px; font-style: italic;">No handover items</td>
                                    </tr>
                                </tbody>
                            </table>

                            <p style="font-size: 12px; color: #212529; margin: 10px 0 4px;"><strong>Other: </strong>{{ form && form.notes ? form.notes : '' }}</p>
                            <p style="font-size: 12px; color: #212529; margin: 0 0 12px;">Include in the lease of unit and to be returned upon departure of unit.</p>

                            <div style="border: 1px solid #ececec; border-radius: 8px; overflow: hidden; margin-bottom: 16px;">
                                <div style="background: #f5f5f5; padding: 10px 16px; border-bottom: 1px solid #ececec;">
                                    <strong style="color: #555; font-size: 13px;">&#9889; Electricity Meter</strong>
                                </div>
                                <div style="padding: 14px 16px; display: flex; gap: 20px;">
                                    <div style="flex: 1;">
                                        <p style="margin: 0 0 8px; font-size: 11px; font-weight: 700; letter-spacing: 0.5px; text-transform: uppercase; color: #28c76f;">&#9679; Check-in</p>
                                        <table style="width: 100%; border-collapse: collapse; color: #212529; font-size: 12px;">
                                            <tr>
                                                <td style="padding: 4px 4px 4px 0; color: #777;">Record</td>
                                                <td style="padding: 4px; font-weight: 600;">{{ form && form.electricity && form.electricity.arrivalRecord ? form.electricity.arrivalRecord : '' }}</td>
                                                <td style="padding: 4px 4px 4px 12px; color: #777;">Date</td>
                                                <td style="padding: 4px; font-weight: 600;">{{ form && form.electricity && form.electricity.arrivalDate ? formatDate(form.electricity.arrivalDate) : '' }}</td>
                                            </tr>
                                        </table>
                                        <div v-if="form && form.electricity && form.electricity.arrivalImage" style="margin-top: 8px;">
                                            <img :src="form.electricity.arrivalImage" style="width: 120px; height: 120px; object-fit: cover; border-radius: 6px; border: 1px solid #ddd; cursor: pointer;" @click="openImagePreview(form.electricity.arrivalImage)" />
                                        </div>
                                    </div>
                                    <div style="flex: 1; border-left: 1px solid #f1f0f5; padding-left: 20px;">
                                        <p style="margin: 0 0 8px; font-size: 11px; font-weight: 700; letter-spacing: 0.5px; text-transform: uppercase; color: #ea5455;">&#9679; Check-out</p>
                                        <table style="width: 100%; border-collapse: collapse; color: #212529; font-size: 12px;">
                                            <tr>
                                                <td style="padding: 4px 4px 4px 0; color: #777;">Record</td>
                                                <td style="padding: 4px; font-weight: 600;">{{ form && form.electricity && form.electricity.departureRecord ? form.electricity.departureRecord : '' }}</td>
                                                <td style="padding: 4px 4px 4px 12px; color: #777;">Date</td>
                                                <td style="padding: 4px; font-weight: 600;">{{ form && form.electricity && form.electricity.departureDate ? formatDate(form.electricity.departureDate) : '' }}</td>
                                            </tr>
                                        </table>
                                        <div v-if="form && form.electricity && form.electricity.departureImage" style="margin-top: 8px;">
                                            <img :src="form.electricity.departureImage" style="width: 120px; height: 120px; object-fit: cover; border-radius: 6px; border: 1px solid #ddd; cursor: pointer;" @click="openImagePreview(form.electricity.departureImage)" />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div style="border: 1px solid #ececec; border-radius: 8px; overflow: hidden; margin-bottom: 16px;">
                                <div style="background: #f5f5f5; padding: 10px 16px; border-bottom: 1px solid #ececec;">
                                    <strong style="color: #555; font-size: 13px;">&#128167; Water Meter</strong>
                                </div>
                                <div style="padding: 14px 16px; display: flex; gap: 20px;">
                                    <div style="flex: 1;">
                                        <p style="margin: 0 0 8px; font-size: 11px; font-weight: 700; letter-spacing: 0.5px; text-transform: uppercase; color: #28c76f;">&#9679; Check-in</p>
                                        <table style="width: 100%; border-collapse: collapse; color: #212529; font-size: 12px;">
                                            <tr>
                                                <td style="padding: 4px 4px 4px 0; color: #777;">Record</td>
                                                <td style="padding: 4px; font-weight: 600;">{{ form && form.water && form.water.arrivalRecord ? form.water.arrivalRecord : '' }}</td>
                                                <td style="padding: 4px 4px 4px 12px; color: #777;">Date</td>
                                                <td style="padding: 4px; font-weight: 600;">{{ form && form.water && form.water.arrivalDate ? formatDate(form.water.arrivalDate) : '' }}</td>
                                            </tr>
                                        </table>
                                        <div v-if="form && form.water && form.water.arrivalImage" style="margin-top: 8px;">
                                            <img :src="form.water.arrivalImage" style="width: 120px; height: 120px; object-fit: cover; border-radius: 6px; border: 1px solid #ddd; cursor: pointer;" @click="openImagePreview(form.water.arrivalImage)" />
                                        </div>
                                    </div>
                                    <div style="flex: 1; border-left: 1px solid #f1f0f5; padding-left: 20px;">
                                        <p style="margin: 0 0 8px; font-size: 11px; font-weight: 700; letter-spacing: 0.5px; text-transform: uppercase; color: #ea5455;">&#9679; Check-out</p>
                                        <table style="width: 100%; border-collapse: collapse; color: #212529; font-size: 12px;">
                                            <tr>
                                                <td style="padding: 4px 4px 4px 0; color: #777;">Record</td>
                                                <td style="padding: 4px; font-weight: 600;">{{ form && form.water && form.water.departureRecord ? form.water.departureRecord : '' }}</td>
                                                <td style="padding: 4px 4px 4px 12px; color: #777;">Date</td>
                                                <td style="padding: 4px; font-weight: 600;">{{ form && form.water && form.water.departureDate ? formatDate(form.water.departureDate) : '' }}</td>
                                            </tr>
                                        </table>
                                        <div v-if="form && form.water && form.water.departureImage" style="margin-top: 8px;">
                                            <img :src="form.water.departureImage" style="width: 120px; height: 120px; object-fit: cover; border-radius: 6px; border: 1px solid #ddd; cursor: pointer;" @click="openImagePreview(form.water.departureImage)" />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <p style="font-size: 12px; color: #212529; margin: 12px 0 0;">** To pay on his/her own bills for the electricity and water supply bills.</p>
                            <div class="prev-kh-sigs">
                                <div class="prev-kh-sig-box">
                                    <p style="text-align: center; margin: 0;">_______________________</p>
                                    <p style="text-align: center; margin: 4px 0 0;">Lessor</p>
                                    <p style="text-align: center; margin: 2px 0 0;">[<span style="display: inline-block; min-width: 180px;">{{ form && form.lessor ? form.lessor : '' }}</span>]</p>
                                </div>
                                <div class="prev-kh-sig-box">
                                    <p style="text-align: center; margin: 0;">_______________________</p>
                                    <p style="text-align: center; margin: 4px 0 0;">Lessee</p>
                                    <p style="text-align: center; margin: 2px 0 0;">[<span style="display: inline-block; min-width: 180px;">{{ form && form.lessee ? form.lessee : '' }}</span>]</p>
                                </div>
                            </div>
                        </div>

                        <!-- ── Tab: Furniture List ── -->
                        <div v-if="previewTab === 'furniture'">
                            <div v-if="!roomPDFPagination.length" class="prev-empty prev-empty-full">
                                No furniture data to preview.
                            </div>
                            <div v-for="(room, rIndex) in roomPDFPagination" :key="'prev-room-' + rIndex">
                                <div
                                    v-for="(block, bIndex) in room.furnitureBlocks"
                                    :key="'prev-block-' + bIndex"
                                    class="prev-paper prev-paper-block"
                                >
                                    <h3 v-if="block.isFirstInRoom" style="margin: 0 0 0 0; font-size: 13px; font-weight: bold; padding: 5px 10px; background: #e0e0e0; border-left: 4px solid #555; color: #222;">
                                        {{ room.name }}
                                    </h3>
                                    <table style="width: 100%; border-collapse: collapse; table-layout: fixed; font-size: 12px;">
                                        <thead>
                                            <tr style="background: #e8e8e8; color: #333; text-align: center;">
                                                <th style="padding: 6px; border: 1px solid #ccc; width: 20%;">Detail</th>
                                                <th style="padding: 6px; border: 1px solid #ccc; width: 40%;">Images</th>
                                                <th style="padding: 6px; border: 1px solid #ccc; width: 25%;">Description</th>
                                                <th style="padding: 6px; border: 1px solid #ccc; width: 15%;"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            <tr style="background: #ffffff; vertical-align: top;">
                                                <td v-if="block.showItemName" style="padding: 5px; border: 1px solid #ccc; word-break: break-word;" v-html="block.item.name.split(',').map(n => n.trim()).join('<br>')"></td>
                                                <td v-else style="padding: 5px; border: 1px solid #ccc; font-size: 11px; color: #666; font-style: italic;" v-html="block.item.name.split(',').map(n => n.trim()).join('<br>') + '<br>(cont.)'"></td>
                                                <td style="padding: 5px; border: 1px solid #ccc;">
                                                    <div v-if="block.furnitureImages && block.furnitureImages.length">
                                                        <div style="font-weight: bold; font-size: 11px; margin-bottom: 3px; color: #333;">Furniture Images</div>
                                                        <div style="display: flex; flex-wrap: wrap; gap: 3px;">
                                                            <div v-for="(img, i) in block.furnitureImages" :key="'pf-'+rIndex+'-'+bIndex+'-'+i" style="width: 48%; height: 130px; overflow: hidden; border: 1px solid #ccc;">
                                                                <img :src="img.url" style="width: 100%; height: 100%; object-fit: cover;" />
                                                            </div>
                                                        </div>
                                                    </div>
                                                    <div v-if="!block.furnitureImages.length && !block.defectImages.length" style="color: #aaa; font-size: 11px;">No images</div>
                                                </td>
                                                <td v-if="block.showItemName" style="padding: 5px; border: 1px solid #ccc; white-space: pre-wrap; word-wrap: break-word; color: #333;">{{ block.item.description || '' }}</td>
                                                <td v-else style="padding: 5px; border: 1px solid #ccc;"></td>
                                                <td v-if="block.showItemName" style="padding: 5px; border: 1px solid #ccc; vertical-align: top; font-size: 11px;">
                                                    <div style="margin: 3px 0;">&#9744; Check-in</div>
                                                    <div style="margin: 3px 0;">&#9744; Check-out</div>
                                                </td>
                                                <td v-else style="padding: 5px; border: 1px solid #ccc;"></td>
                                            </tr>
                                            <tr v-if="block.defectImages && block.defectImages.length" style="background: #ffffff; vertical-align: top;">
                                                <td style="padding: 5px; border: 1px solid #ccc;"></td>
                                                <td style="padding: 5px; border: 1px solid #ccc;">
                                                    <div style="font-weight: bold; font-size: 11px; margin-bottom: 3px; color: #333;">Defect Images</div>
                                                    <div style="display: flex; flex-wrap: wrap; gap: 3px;">
                                                        <div v-for="(img, i) in block.defectImages" :key="'pd-'+rIndex+'-'+bIndex+'-'+i" style="width: 48%; height: 130px; overflow: hidden; border: 1px solid #ccc;">
                                                            <img :src="img.url" style="width: 100%; height: 100%; object-fit: cover;" />
                                                        </div>
                                                    </div>
                                                </td>
                                                <td style="padding: 5px; border: 1px solid #ccc;"></td>
                                                <td style="padding: 5px; border: 1px solid #ccc;"></td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>

                        <!-- ── Tab: Footer ── -->
                        <div v-if="previewTab === 'footer'" class="prev-paper" style="font-size: 12px; color: #212529; line-height: 1.6;">
                            <p>This inventory form is for your protection. It helps establish the condition of your apartment at the time of your arrival, and should be completed and returned to your landlord within the first three days of occupancy. Be specific and check carefully when completing this form.</p>
                            <p>Look for dust, grease, stains, burns, damages, and wear and tear. Cross out items that do not apply and additional paper if more space is needed.</p>
                            <p><strong>NOTE:</strong> Copies of the entire inventory, including any attachments, should be provided to lessor and lessee.</p>
                            <div class="prev-kh-sigs" style="margin-top: 40px;">
                                <div class="prev-kh-sig-box">
                                    <p style="text-align: center; margin: 0;">_______________________</p>
                                    <p style="text-align: center; margin: 4px 0 0;">Lessor</p>
                                    <p style="text-align: center; margin: 2px 0 0;">[<span style="display: inline-block; min-width: 180px;">{{ form ? form.lessor : '' }}</span>]</p>
                                </div>
                                <div class="prev-kh-sig-box">
                                    <p style="text-align: center; margin: 0;">_______________________</p>
                                    <p style="text-align: center; margin: 4px 0 0;">Lessee</p>
                                    <p style="text-align: center; margin: 2px 0 0;">[<span style="display: inline-block; min-width: 180px;">{{ form ? form.lessee : '' }}</span>]</p>
                                </div>
                            </div>
                        </div>

                    </div>
                </div>
            </div>

            <!-- ── Image Lightbox ── -->
            <div v-if="showImagePreview" class="img-lightbox-overlay" @click.self="showImagePreview = false">
                <button class="img-lightbox-close" @click="showImagePreview = false">&#215;</button>
                <img :src="imagePreviewUrl" class="img-lightbox-img" />
            </div>

            <!-- Room Tabs -->
            <draggable
                v-model="rooms"
                tag="div"
                class="inv-tabs-bar"
                :animation="150"
                ghost-class="inv-tab-ghost"
                chosen-class="inv-tab-chosen"
                handle=".inv-tab-handle"
                @end="saveRoomOrder"
            >
                <button
                    v-for="room in rooms"
                    :key="room.id"
                    class="inv-tab"
                    :class="{ 'inv-tab-active': activeRoomId === room.id }"
                    @click="selectRoom(room.id)"
                >
                    <span class="inv-tab-handle" title="Drag to reorder">&#8942;&#8942;</span>
                    {{ room.name }}
                </button>
            </draggable>

            <!-- Room Panel -->
            <div v-for="room in rooms" :key="room.id" v-show="activeRoomId === room.id" class="inv-panel">

                <!-- Room Header -->
                <div class="inv-room-header">
                    <div class="inv-room-title-wrap">
                        <span v-if="editingRoomNameId !== room.id" class="inv-room-title">{{ room.name }}</span>
                        <input
                            v-else
                            v-model="editedRoomName"
                            @keyup.enter="saveRoomName(room)"
                            @blur="saveRoomName(room)"
                            class="inv-room-input"
                            autofocus
                        />
                        <button v-if="editingRoomNameId !== room.id" class="inv-icon-btn" @click="startEditRoomName(room)" title="Rename">&#9998;</button>
                    </div>
                    <button class="inv-btn inv-btn-sm inv-btn-danger-ghost" @click="deleteRoom(room)">Delete Room</button>
                </div>

                <!-- Toggle furniture -->
                <button v-if="room.furniture.length" class="inv-toggle" @click="toggleRoom(room.id)">
                    <span>{{ isRoomExpanded(room.id) ? '▼' : '▶' }}</span>
                    {{ isRoomExpanded(room.id) ? 'Hide' : 'Show' }} Furniture
                    <span class="inv-badge">{{ room.furniture.length }}</span>
                </button>

                <!-- Furniture Cards -->
                <div v-if="isRoomExpanded(room.id)" class="inv-furniture-list">
                    <div v-for="item in room.furniture" :key="item.id" class="inv-furniture-card">
                        <div class="inv-furniture-top">
                            <span class="inv-furniture-name">{{ item.name }}</span>
                            <div class="inv-furniture-actions">
                                <button class="inv-btn inv-btn-sm inv-btn-warning" @click="editFurniture(room.id, item)">Edit</button>
                                <button class="inv-btn inv-btn-sm inv-btn-danger" @click="deleteFurniture(room.id, item.id)">Delete</button>
                            </div>
                        </div>

                        <pre v-if="item.description" class="inv-desc">{{ item.description }}</pre>

                        <div v-if="item.imageUrls.length || item.defectImageUrls.length" class="inv-img-section">
                            <div v-if="item.imageUrls.length" class="inv-img-group">
                                <div class="inv-img-label">Furniture Images</div>
                                <div class="inv-img-grid">
                                    <img v-for="(img, i) in item.imageUrls" :key="'img'+i" :src="img.url" class="inv-thumb" @click="openImagePreview(img.url)" />
                                </div>
                            </div>
                            <div v-if="item.defectImageUrls.length" class="inv-img-group inv-img-group-defect">
                                <div class="inv-img-label inv-img-label-defect">Defect Images</div>
                                <div class="inv-img-grid">
                                    <img v-for="(img, i) in item.defectImageUrls" :key="'d'+i" :src="img.url" class="inv-thumb" @click="openImagePreview(img.url)" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Add / Edit Form -->
                <div :ref="'editForm-' + room.id" class="inv-form-panel">
                    <div class="inv-form-header">
                        <span class="inv-form-title">{{ isEditing ? '&#9998; Edit Furniture' : '+ Add Furniture' }}</span>
                    </div>
                    <form @submit.prevent="saveFurniture(room.id)" class="inv-form">
                        <div class="inv-field">
                            <label class="inv-label">Furniture Name <span class="inv-required">*</span></label>
                            <input v-model="furnitureForm.name" class="inv-input" required placeholder="e.g. Sofa, Dining table..." />
                        </div>
                        <div class="inv-field">
                            <label class="inv-label">Description</label>
                            <textarea v-model="furnitureForm.description" class="inv-input inv-textarea" rows="3" placeholder="Condition, notes, brand..."></textarea>
                        </div>
                        <div class="inv-field-row">
                            <div class="inv-field">
                                <label class="inv-label">Furniture Images</label>
                                <label class="inv-upload-area">
                                    <input type="file" style="display:none;" @change="handleMultiUpload($event, 'imageUrls')" multiple />
                                    <span>&#128247; Click to upload images</span>
                                </label>
                                <div class="inv-preview-grid">
                                    <div v-for="(img, i) in furnitureForm.imageUrls" :key="'fimg'+i" class="inv-preview-wrap">
                                        <img :src="img.url || img.preview" class="inv-preview-img" @click="openImagePreview(img.url || img.preview)" />
                                        <button type="button" class="inv-remove-img" @click="removeImage(i, 'imageUrls', img.id)">&#215;</button>
                                    </div>
                                </div>
                            </div>
                            <div class="inv-field">
                                <label class="inv-label inv-label-defect">Defect Images</label>
                                <label class="inv-upload-area inv-upload-area-defect">
                                    <input type="file" style="display:none;" @change="handleMultiUpload($event, 'defectImageUrls')" multiple />
                                    <span>&#9888; Click to upload defect images</span>
                                </label>
                                <div class="inv-preview-grid">
                                    <div v-for="(img, i) in furnitureForm.defectImageUrls" :key="'dimg'+i" class="inv-preview-wrap">
                                        <img :src="img.url || img.preview" class="inv-preview-img" @click="openImagePreview(img.url || img.preview)" />
                                        <button type="button" class="inv-remove-img" @click="removeImage(i, 'defectImageUrls', img.id)">&#215;</button>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div class="inv-form-actions">
                            <button type="submit" class="inv-btn inv-btn-primary">
                                {{ isEditing ? 'Update Furniture' : 'Add Furniture' }}
                            </button>
                            <button v-if="isEditing" type="button" class="inv-btn inv-btn-outline" @click="cancelEdit">Cancel</button>
                        </div>
                    </form>
                </div>

            </div>
        </div>

        <!-- Hidden Footer PDF content (logic unchanged) -->
        <div v-if="preparingPDFFooter" ref="pdfContent" style="padding: 20px; background: white;">
            <p style="page-break-inside: avoid; break-inside: avoid;">{{ `This inventory form is for your protection. It helps establish the condition of your apartment at the time of your arrival, and should be completed and returned to your landlord within the first three days of occupancy. Be specific and check carefully when completing this form.` }}</p>
            <p style="page-break-inside: avoid; break-inside: avoid;">{{ `Look for dust, grease, stains, burns, damages, and wear and tear. Cross out items that do not apply and additional paper if more space is needed.` }}</p>
            <p style="page-break-inside: avoid; break-inside: avoid;"><strong>NOTE:</strong> Copies of the entire inventory, including any attachments, should be provided to lessor and lessee.</p>
            <div class="mt-5 d-flex justify-content-between">
                <div>
                    <p style="text-align: center;">_______________________</p>
                    <p style="text-align: center;">Lessor</p>
                    <p style="text-align: center;">[<span style="display: inline-block; min-width: 180px;">{{ form ? form.lessor : '' }}</span>]</p>
                </div>
                <div>
                    <p style="text-align: center;">_______________________</p>
                    <p style="text-align: center;">Lessee</p>
                    <p style="text-align: center;">[<span style="display: inline-block; min-width: 180px;">{{ form ? form.lessee : '' }}</span>]</p>
                </div>
            </div>
        </div>

    </div>
</template>

<script>
import {
    BCard, BButton,
} from 'bootstrap-vue'
import BCardActions from '@core/components/b-card-actions/BCardActions.vue'
import html2pdf from 'html2pdf.js';
import router from '@/router'
import axios from '@axios'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import draggable from 'vuedraggable';

export default {
    components: {
        BCardActions,
        BCard, BButton,
        draggable,
    },
    directives: {
        'click-outside': {
            bind(el, binding) {
                el._clickOutside = (e) => { if (!el.contains(e.target)) binding.value(e); };
                document.addEventListener('click', el._clickOutside);
            },
            unbind(el) {
                document.removeEventListener('click', el._clickOutside);
            },
        },
    },
    computed: {
        currentLocale() {
            return this.$i18n.locale
        },
    },
    data() {
        const userStorage = JSON.parse(localStorage.getItem('userData'))
        const property_id = router.currentRoute.query.propertyId

        return {
            newRoomName: '',
            rooms: [],
            roomId: 1,
            furnitureId: 1,
            activeRoomId: null,
            isEditing: false,
            editingRoomId: null,
            editingFurnitureId: null,
            furnitureForm: {
                room_id: '',
                name: '',
                description: '',
                amount: 1,
                imageUrls: [],
                defectImageUrls: []
            },
            expandedRoomIds: [],
            removeImages: [],
            editingRoomNameId: null,
            editedRoomName: '',
            propertyData: [],
            propertyId: property_id,
            form: {
                propertyId: property_id,
                lessor: '',
                lessee: '',
                date: '',
                notes: '',
                electricity: {
                    arrivalRecord: '',
                    arrivalDate: '',
                    departureRecord: '',
                    departureDate: '',
                },
                water: {
                    arrivalRecord: '',
                    arrivalDate: '',
                    departureRecord: '',
                    departureDate: '',
                },
                handoverItems: [],
                created_by: userStorage.id,
                updated_by: userStorage.id
            },
            roomPDFPagination: [],
            preparingPDFInventory: false,
            preparingPDFFooter: false,
            downloadingAll: false,
            showDownloadMenu: false,
            existingActiveRoomId: null,
            showPreview: false,
            previewTab: 'keyhand',
            showImagePreview: false,
            imagePreviewUrl: '',
        }
    },
    methods: {
        toggleDownloadMenu() {
            this.showDownloadMenu = !this.showDownloadMenu;
        },
        closeDownloadMenu() {
            this.showDownloadMenu = false;
        },
        async downloadOne(type) {
            this.showDownloadMenu = false;
            if (type === 'keyhand') {
                this.$router.push({ name: 'keyhand-add', params: { id: this.propertyId }, query: { autoDownload: 'true' } });
            } else if (type === 'footer') {
                await this.exportPDFFooter();
            } else if (type === 'furniture') {
                await this.exportToPDF();
            } else if (type === 'all') {
                await this.downloadAllPDFs();
            }
        },
        openPreview() {
            this.roomPagination();
            this.previewTab = 'keyhand';
            this.showPreview = true;
        },
        async exportPDFFooter() {
            this.preparingPDFFooter = true;
            await this.$nextTick();

            const element = this.$refs.pdfContent;
            const opt = {
                margin: 0.5,
                filename: 'footer-inventory.pdf',
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2, useCORS: true },
                jsPDF: { unit: 'in', format: 'a4', orientation: 'portrait' }
            };

            await html2pdf().from(element).set(opt).save();

            this.preparingPDFFooter = false;
        },
        async downloadAllPDFs() {
            this.downloadingAll = true;
            try {
                await this.exportPDFFooter();
                await this.exportToPDF();
                this.$router.push({
                    name: 'keyhand-add',
                    params: { id: this.propertyId },
                    query: { autoDownload: 'true' }
                });
            } finally {
                this.downloadingAll = false;
            }
        },
        roomPagination() {
            const MAX_IMAGES = 4;
            const chunk = (arr, size) => {
                const out = [];
                for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
                return out;
            };

            this.roomPDFPagination = this.rooms
                .filter(room => (room.furniture || room.furnitures || []).length > 0)
                .map(room => {
                    const furniture = room.furniture || room.furnitures || [];
                    const furnitureBlocks = [];

                    furniture.forEach(item => {
                        const fImgs = (item.imageUrls || []).map(img => ({ url: img.url }));
                        const dImgs = (item.defectImageUrls || []).map(img => ({ url: img.url }));

                        // Furniture image chunks — always at least one (may be empty)
                        const fChunks = fImgs.length > 0 ? chunk(fImgs, MAX_IMAGES) : [[]];
                        // Defect image chunks
                        const dChunks = dImgs.length > 0 ? chunk(dImgs, MAX_IMAGES) : [];

                        // Emit all furniture chunks
                        fChunks.forEach((fc, ci) => {
                            const isLast = ci === fChunks.length - 1;
                            furnitureBlocks.push({
                                item,
                                furnitureImages: fc,
                                // Attach first defect chunk to the last furniture canvas (same table)
                                defectImages: isLast && dChunks.length > 0 ? dChunks[0] : [],
                                isFirstInRoom: furnitureBlocks.length === 0,
                                showItemName: ci === 0,
                            });
                        });

                        // Remaining defect chunks (beyond the first) get their own canvases
                        for (let di = 1; di < dChunks.length; di++) {
                            furnitureBlocks.push({
                                item,
                                furnitureImages: [],
                                defectImages: dChunks[di],
                                isFirstInRoom: false,
                                showItemName: false,
                            });
                        }
                    });

                    return { ...room, furnitureBlocks };
                });
        },
        getFurniturePages(furniture) {
            const pages = {};

            for (const key in furniture) {
                if (key.startsWith('page')) {
                    pages[key] = furniture[key];
                }
            }
            return pages;
        },
        getAllImages(furniture) {
            const normal = (furniture.imageUrls || []).map(img => ({ ...img, type: 'normal' }));
            const defects = (furniture.defectImageUrls || []).map(img => ({ ...img, type: 'defect' }));
            return [...normal, ...defects];
        },
        selectRoom(id) {
            this.activeRoomId = id
        },
        resetForm() {
            this.furnitureForm = {
                room_id: '',
                name: '',
                description: '',
                // condition_item: '',
                amount: 1,
                imageUrls: [],
                defectImageUrls: [],
            }
        },
        saveRoomOrder() {
            const order = this.rooms.map(r => r.id);
            localStorage.setItem(`room-order-${this.propertyId}`, JSON.stringify(order));
        },
        async getRoomData() {
            try {
                const property_id = router.currentRoute.query.propertyId
                const response = await axios.get(`/furniture-list/v1/room/get/${property_id}`)

                if(response.data.success){
                    this.rooms = response.data.data.map(room => ({
                        ...room,
                        furniture: room.furnitures || []
                    }))

                    const savedOrder = JSON.parse(localStorage.getItem(`room-order-${this.propertyId}`) || 'null');
                    if (savedOrder && savedOrder.length) {
                        this.rooms.sort((a, b) => {
                            const ai = savedOrder.indexOf(a.id);
                            const bi = savedOrder.indexOf(b.id);
                            if (ai === -1) return 1;
                            if (bi === -1) return -1;
                            return ai - bi;
                        });
                    }

                    this.roomPagination()

                    if (this.rooms.length > 0) {
                        this.existingActiveRoomId = this.activeRoomId
                        this.activeRoomId = this.existingActiveRoomId? this.existingActiveRoomId: this.rooms[0].id;
                        this.expandedRoomIds = this.rooms.map(r => r.id);
                    } else {
                        this.activeRoomId = null;
                        this.expandedRoomIds = [];
                    }
                }
            } catch (err) {
                alert('Failed to save furniture to database')
            }
        },
        async addRoom() {
            try {
                if(this.newRoomName == ''){
                    this.newRoomName = 'Room Title'
                }
                
                const userStorage = JSON.parse(localStorage.getItem('userData'))
                const property_id = router.currentRoute.query.propertyId
                const setData = {
                    created_by: userStorage.id,
                    updated_by: userStorage.id,
                    property_id: property_id,
                    name: this.newRoomName

                }
                const response = await axios.post('/furniture-list/v1/room/add', setData)
                if(response.data.success){
                    this.getRoomData()
                    this.newRoomName = ''
                }
                
            } catch (err) {
                alert('Failed to save furniture to database')
            }
        },
        async saveFurniture(roomId) {
            const room = this.rooms.find(r => r.id === roomId)
            if (!room) return

            this.furnitureForm.room_id = roomId
            const formData = new FormData();
            const data = { ...this.furnitureForm };

            delete data.imageUrls;
            delete data.defectImageUrls;

            if (this.removeImages?.length > 0) {
                data.removeImages = this.removeImages;
            }

            formData.append("data", JSON.stringify(data));

            if (this.furnitureForm.imageUrls && this.furnitureForm.imageUrls.length > 0) {
                this.furnitureForm.imageUrls.forEach((imgObj) => {
                    formData.append("imageUrls", imgObj.file);
                });
            }

            if (this.furnitureForm.defectImageUrls && this.furnitureForm.defectImageUrls.length > 0) {
                this.furnitureForm.defectImageUrls.forEach((imgObj) => {
                    formData.append("defectImageUrls", imgObj.file);
                });
            }

            try {
                if (this.isEditing) {
                    await axios.post(`/furniture-list/v1/furniture/update/${this.editingFurnitureId}`, formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    });

                    this.removeImages = [];
                    this.isEditing = false;
                    this.editingFurnitureId = null;
                } else {
                    await axios.post('/furniture-list/v1/furniture/add', formData, {
                        headers: {
                            'Content-Type': 'multipart/form-data'
                        }
                    });
                }

                this.cancelEdit();
                this.getRoomData();
            } catch (error) {
                console.error('Save failed', error);
            }
        },
        editFurniture(roomId, item) {
            this.furnitureForm = {
                name: item.name || '',
                description: item.description || '',
                // condition_item: item.condition_item || '',
                amount: item.amount || 1,
                imageUrls: (item.imageUrls || []).map(img => ({
                    id: img.id || '',
                    file: null,
                    url: img.url || img
                })),
                defectImageUrls: (item.defectImageUrls || []).map(img => ({
                    id: img.id || '',
                    file: null,
                    url: img.url || img
                })),
                room_id: roomId
            };

            this.isEditing = true;
            this.editingRoomId = roomId;
            this.editingFurnitureId = item.id;

            this.$nextTick(() => {
                const target = this.$refs['editForm-' + roomId];
                const el = Array.isArray(target) ? target[0] : target;

                if (el && el.scrollIntoView) {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
            });
        },
        async deleteFurniture(roomId, furnitureId) {
            try {
                const confirmed = await this.$bvModal.msgBoxConfirm(
                    `Are you sure you want to delete this furniture item?`,
                    {
                        title: 'Please Confirm',
                        size: 'sm',
                        okVariant: 'danger',
                        okTitle: 'Yes',
                        cancelTitle: 'No',
                        cancelVariant: 'outline-secondary',
                        hideHeaderClose: false,
                        centered: true,
                    }
                );

                if (!confirmed) return;

                const response = await axios.delete(`/furniture-list/v1/furniture/delete/${furnitureId}`)

                if (response.data.success) {
                    this.$toast({
                        component: ToastificationContent,
                        position: 'top-right',
                        props: {
                            title: 'Success',
                            icon: 'BellIcon',
                            variant: 'success',
                            text: response.data.message,
                        },
                    });

                    const room = this.rooms.find(r => r.id === roomId);
                    if (room) {
                        room.furniture = room.furniture.filter(f => f.id !== furnitureId);
                    } else {
                        // fallback: reload all room data
                        await this.getRoomData();
                    }
                } else {
                    this.$toast({
                        component: ToastificationContent,
                        position: 'top-right',
                        props: {
                            title: 'Error',
                            icon: 'BellIcon',
                            variant: 'danger',
                            text: response.data.message || 'Failed to delete the room.',
                        },
                    });
                }

                this.getRoomData();
            } catch (error) {
                this.$toast({
                    component: ToastificationContent,
                    position: 'top-right',
                    props: {
                        title: 'Error',
                        icon: 'BellIcon',
                        variant: 'danger',
                        text: error.response?.data?.message || 'There was a problem deleting the room.',
                    },
                });
            }
        },
        cancelEdit() {
            this.isEditing = false
            this.editingRoomId = null
            this.editingFurnitureId = null
            this.resetForm()
        },
        handleMultiUpload(event, key) {
            const files = Array.from(event.target.files || []);
            if (!Array.isArray(this.furnitureForm[key])) {
                this.furnitureForm[key] = [];
            }

            files.forEach(file => {
                const previewUrl = URL.createObjectURL(file);
                this.furnitureForm[key].push({
                    file,
                    preview: previewUrl
                });
            });

            event.target.value = null;
        },
        removeImage(index, key, imageId) {
            if(imageId) this.removeImages.push(imageId)
            const img = this.furnitureForm[key][index];
            if (img && img.preview) URL.revokeObjectURL(img.preview);

            this.furnitureForm[key].splice(index, 1);
        },
        openImagePreview(url) {
            if (!url) return;
            this.imagePreviewUrl = url;
            this.showImagePreview = true;
        },
        incrementAmount() {
            if (!this.furnitureForm.amount || this.furnitureForm.amount < 1) {
                this.furnitureForm.amount = 1;
            } else {
                this.furnitureForm.amount++;
            }
        },
        decrementAmount() {
            if (!this.furnitureForm.amount || this.furnitureForm.amount <= 1) {
                this.furnitureForm.amount = 1;
            } else {
                this.furnitureForm.amount--;
            }
        },
        async deleteRoom(room) {
            try {
                const confirmed = await this.$bvModal.msgBoxConfirm(
                    `Are you sure you want to delete ${room.name} and all its furniture?`,
                    {
                        title: 'Please Confirm',
                        size: 'sm',
                        okVariant: 'danger',
                        okTitle: 'Yes',
                        cancelTitle: 'No',
                        cancelVariant: 'outline-secondary',
                        hideHeaderClose: false,
                        centered: true,
                    }
                );

                if (!confirmed) return;

                const response = await axios.delete(`/furniture-list/v1/room/delete/${room.id}`)

                if (response.data.success) {
                    this.$toast({
                        component: ToastificationContent,
                        position: 'top-right',
                        props: {
                            title: 'Success',
                            icon: 'BellIcon',
                            variant: 'success',
                            text: response.data.message,
                        },
                    });
                } else {
                    this.$toast({
                        component: ToastificationContent,
                        position: 'top-right',
                        props: {
                            title: 'Error',
                            icon: 'BellIcon',
                            variant: 'danger',
                            text: response.data.message || 'Failed to delete the room.',
                        },
                    });
                }

                this.getRoomData();
            } catch (error) {
                this.$toast({
                    component: ToastificationContent,
                    position: 'top-right',
                    props: {
                        title: 'Error',
                        icon: 'BellIcon',
                        variant: 'danger',
                        text: error.response?.data?.message || 'There was a problem deleting the room.',
                    },
                });
            }
        },
        toggleRoom(roomId) {
            const index = this.expandedRoomIds.indexOf(roomId);
            if (index !== -1) {
                this.expandedRoomIds.splice(index, 1);
            } else {
                this.expandedRoomIds.push(roomId);
            }
        },
        isRoomExpanded(roomId) {
            return this.expandedRoomIds.includes(roomId);
        },
        waitForImagesLoad(container) {
            const imgs = Array.from(container.querySelectorAll('img'));
            return Promise.all(imgs.map(img => new Promise(resolve => {
                if (img.complete) resolve();
                else img.onload = img.onerror = resolve;
            })));
        },
        waitForImagesToLoad(container) {
            const images = container.querySelectorAll('img');
            const promises = Array.from(images).map(img => {
            if (img.complete && img.naturalHeight !== 0) return Promise.resolve();
                return new Promise(resolve => {
                    img.onload = img.onerror = resolve;
                });
            });
            return Promise.all(promises);
        },
        async exportToPDF() {
            this.preparingPDFInventory = true;
            await this.$nextTick();

            const pdf = new jsPDF({ unit: 'px', format: 'a4', orientation: 'portrait' });
            const pdfWidth = pdf.internal.pageSize.getWidth();
            const pdfHeight = pdf.internal.pageSize.getHeight();
            const margin = 20;
            const printableWidth = pdfWidth - margin * 2;
            const printableHeight = pdfHeight - margin * 2;

            const pageElements = this.$refs.pdfPage;
            if (!pageElements || pageElements.length === 0) {
                console.error('No PDF pages found');
                this.preparingPDFInventory = false;
                return;
            }

            const images = [];
            for (const el of pageElements) {
                await this.waitForImagesToLoad(el);
                const canvas = await html2canvas(el, { scale: 2, useCORS: true });
                const imgData = canvas.toDataURL('image/jpeg', 1.0);
                const imgProps = pdf.getImageProperties(imgData);
                const scaleFactor = printableWidth / imgProps.width;
                const imgHeight = imgProps.height * scaleFactor;
                images.push({ imgData, imgHeight });
            }

            const generatedPages = [];
            let currentPage = [];
            let currentY = 0;

            for (const img of images) {
                if (currentY + img.imgHeight > printableHeight && currentY > 0) {
                    generatedPages.push(currentPage);
                    currentPage = [];
                    currentY = 0;
                }
                currentPage.push(img);
                currentY += img.imgHeight + margin;
            }

            if (currentPage.length > 0) generatedPages.push(currentPage);

            const totalPages = generatedPages.length;
            let pageNumber = 1;

            for (let i = 0; i < generatedPages.length; i++) {
                const page = generatedPages[i];
                if (i > 0) pdf.addPage();

                let y = margin;
                for (const img of page) {
                    pdf.addImage(img.imgData, 'JPEG', margin, y, printableWidth, img.imgHeight);
                    y += img.imgHeight + margin;
                }

                pdf.setFontSize(10);
                pdf.text(`Page ${pageNumber} of ${totalPages}`, pdfWidth - margin, pdfHeight - margin, { align: 'right' });
                pageNumber++;
            }

            pdf.save('inventory.pdf');
            this.preparingPDFInventory = false;
        },
        async initializeDefaultRooms() {
            const defaultNames = ['Bedroom1', 'Bedroom2', 'Bedroom3', 'Living', 'Kitchen', 'Maid']
            for (const name of defaultNames) {
                this.newRoomName = name
                await this.addRoom()
            }
        },
        startEditRoomName(room) {
            this.editingRoomNameId = room.id;
            this.editedRoomName = room.name;
            this.$nextTick(() => {
                const input = document.querySelector('input.form-control-sm');
                if (input) input.focus();
            });
        },
        async saveRoomName(room) {
            if (this.editedRoomName.trim() === '' || this.editedRoomName === room.name) {
                this.editingRoomNameId = null;
                return;
            }

            try {
                const user = JSON.parse(localStorage.getItem('userData'));

                const payload = {
                    id: room.id,
                    name: this.editedRoomName,
                    updated_by: user.id,
                };

                const response = await axios.post(`/furniture-list/v1/room/update`, payload);

                if (response.data.success) {
                    this.$toast({
                        component: ToastificationContent,
                        position: 'top-right',
                        props: {
                            title: 'Room Name Updated',
                            icon: 'EditIcon',
                            variant: 'success',
                            text: response.data.message || 'Room name updated successfully',
                        },
                    });

                    await this.getRoomData();
                } else {
                    throw new Error(response.data.message || 'Failed to update');
                }
            } catch (err) {
                this.$toast({
                    component: ToastificationContent,
                    position: 'top-right',
                    props: {
                        title: 'Error',
                        icon: 'AlertTriangleIcon',
                        variant: 'danger',
                        text: err.message || 'Failed to update room name',
                    },
                });
            } finally {
                this.editingRoomNameId = null;
                this.editedRoomName = '';
            }
        },
        async getPropertyData(){
            try {
                const property_id = router.currentRoute.query.propertyId

                const response = await axios.post(`/cache-master/v1/property-filter`, {
                    perpage: 10,
                    offset: 0,
                    byID: property_id
                });

                if (response.data.success) {
                    this.propertyData = response?.data?.data?.[0]
                } else {
                    this.$toast({
                        component: ToastificationContent,
                        position: 'top-right',
                        props: {
                            title: 'Error',
                            icon: 'BellIcon',
                            variant: 'danger',
                            text: response.data.message || 'Failed to delete the room.',
                        },
                    });
                }
            } catch(error) {
                console.error('Error fetch property data:', err)
            }
        },
        async fetchingKeyHand(){
            try {
                const response = await axios.get(`/handover/v1/get/${this.propertyId}`);

                if (response.data.success) {
                    this.form = response?.data?.data[0]
                } else {
                    this.$toast({
                        component: ToastificationContent,
                        position: 'top-right',
                        props: {
                            title: 'Error',
                            icon: 'BellIcon',
                            variant: 'danger',
                            text: response.data.message || 'Failed to fetch the handover.',
                        },
                    });
                }
            } catch(error) {
                this.$toast({
                    component: ToastificationContent,
                    position: 'top-right',
                    props: {
                        title: 'Error',
                        icon: 'BellIcon',
                        variant: 'danger',
                        text: error.response?.data?.message || 'Something went wrong.',
                    },
                });
            }
        },
        formatDate(date) {
            if (!date) return '';
            const d = new Date(date);

            const day = d.getDate();
            const monthNames = [
                'January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'
            ];
            const month = monthNames[d.getMonth()];
            const year = d.getFullYear();

            return `${day} ${month} ${year}`;
        },
        getAllImages(furniture) {
            const furnitureImages = (furniture.imageUrls || []).map(img => ({
                url: img.url,
                type: 'furniture'
            }));
            const defectImages = (furniture.defectImageUrls || []).map(img => ({
                url: img.url,
                type: 'defect'
            }));
            return [...furnitureImages, ...defectImages];
        },
         checkImageOrientation(img, event) {
            const el = event.target;
            if (el.naturalWidth > el.naturalHeight) {
                this.$set(img, 'landscape', true);
            } else {
                this.$set(img, 'landscape', false);
            }
        },
    },
    mounted() {
        this.expandedRoomIds = this.rooms.map(r => r.id);
    },
    created() {
        this.getRoomData().then(() => {
            if (this.rooms.length === 0) {
                this.initializeDefaultRooms()
            }
        });

        this.getPropertyData()
        this.fetchingKeyHand()
    }
}
</script>

<style scoped>
/* ── Page shell ── */
.inv-page {
  font-family: Arial, sans-serif;
  font-size: 14px;
  color: #5e5873;
  background: #f8f8f8;
  min-height: 100vh;
}
.inv-wrapper {
  max-width: 1100px;
  margin: 0 auto;
  padding: 20px 16px;
}

/* ── Top bar ── */
.inv-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #80AC03;
  color: #fff;
  padding: 12px 20px;
  border-radius: 10px;
  margin-bottom: 16px;
  flex-wrap: wrap;
  gap: 10px;
}
.inv-topbar-title {
  font-size: 16px;
  font-weight: bold;
  letter-spacing: 0.5px;
}
.inv-topbar-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

/* ── Buttons ── */
.inv-btn {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 7px 14px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  border: none;
  text-decoration: none;
  white-space: nowrap;
  transition: opacity 0.15s;
}
.inv-btn:hover { opacity: 0.85; }
.inv-btn-primary  { background: #80AC03; color: #fff; }
.inv-btn-outline  { background: transparent; color: #fff; border: 1px solid rgba(255,255,255,0.5); }
.inv-btn-download-all { background: #28c76f; color: #fff; }
.inv-btn-download-all:disabled { opacity: 0.6; cursor: not-allowed; }

/* Download dropdown */
.inv-dropdown-wrap {
  position: relative;
}
.inv-dropdown-menu {
  position: absolute;
  top: calc(100% + 6px);
  right: 0;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.15);
  border: 1px solid #ebe9f1;
  min-width: 180px;
  z-index: 1000;
  overflow: hidden;
}
.inv-dropdown-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  background: none;
  border: none;
  padding: 10px 16px;
  font-size: 13px;
  color: #5e5873;
  cursor: pointer;
  text-align: left;
  transition: background 0.1s;
}
.inv-dropdown-item:hover { background: #f4f8e6; color: #80AC03; }
.inv-dropdown-divider { height: 1px; background: #ebe9f1; margin: 2px 0; }
.inv-dropdown-item-all { color: #28c76f; font-weight: 600; }
.inv-dropdown-item-all:hover { background: #f0fff6; color: #28c76f; }
.inv-btn-warning  { background: #ff9f43; color: #fff; }
.inv-btn-danger   { background: #ea5455; color: #fff; }
.inv-btn-danger-ghost { background: transparent; color: #ea5455; border: 1px solid #ea5455; }
.inv-btn-sm { padding: 5px 10px; font-size: 12px; }
.inv-icon-btn {
  background: none;
  border: none;
  cursor: pointer;
  color: #6b7280;
  font-size: 16px;
  padding: 2px 6px;
  border-radius: 4px;
}
.inv-icon-btn:hover { background: #f8f8f8; color: #80AC03; }

/* ── Room Tabs ── */
.inv-tabs-bar {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  margin-bottom: 16px;
  border-bottom: 2px solid #d0d7de;
  padding-bottom: 0;
}
.inv-tab {
  padding: 8px 16px;
  border: none;
  background: none;
  cursor: pointer;
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
  border-bottom: 3px solid transparent;
  margin-bottom: -2px;
  border-radius: 4px 4px 0 0;
  transition: color 0.15s, border-color 0.15s;
}
.inv-tab:hover { color: #80AC03; }
.inv-tab-handle {
  display: inline-block;
  cursor: grab;
  font-size: 10px;
  letter-spacing: -2px;
  color: #c0bfcf;
  margin-right: 4px;
  line-height: 1;
  vertical-align: middle;
}
.inv-tab-handle:active { cursor: grabbing; }
.inv-tab-ghost {
  opacity: 0.4;
  background: rgba(128,172,3,0.08);
  border-bottom-color: #80AC03 !important;
}
.inv-tab-chosen {
  background: rgba(128,172,3,0.06);
  box-shadow: 0 2px 8px rgba(128,172,3,0.2);
}
.inv-tab-active {
  color: #80AC03;
  border-bottom-color: #80AC03;
  font-weight: 700;
  background: #fff;
}

/* ── Room Panel ── */
.inv-panel {
  background: #fff;
  border-radius: 10px;
  border: 1px solid #d0d7de;
  overflow: hidden;
}

/* ── Room Header ── */
.inv-room-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 20px;
  border-bottom: 1px solid #e4e7eb;
  background: #f9fafb;
}
.inv-room-title-wrap {
  display: flex;
  align-items: center;
  gap: 8px;
}
.inv-room-title {
  font-size: 18px;
  font-weight: 700;
  color: #80AC03;
}
.inv-room-input {
  font-size: 16px;
  font-weight: 600;
  border: 1px solid #80AC03;
  border-radius: 6px;
  padding: 4px 10px;
  outline: none;
  min-width: 180px;
}

/* ── Toggle ── */
.inv-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: none;
  border: none;
  cursor: pointer;
  color: #80AC03;
  font-size: 13px;
  font-weight: 600;
  padding: 10px 20px;
}
.inv-badge {
  background: #80AC03;
  color: #fff;
  border-radius: 10px;
  font-size: 11px;
  padding: 1px 7px;
}

/* ── Furniture List ── */
.inv-furniture-list {
  padding: 0 16px 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.inv-furniture-card {
  border: 1px solid #e4e7eb;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
  transition: box-shadow 0.15s;
}
.inv-furniture-card:hover { box-shadow: 0 2px 10px rgba(0,0,0,0.08); }
.inv-furniture-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  background: #f5f7fa;
  border-bottom: 1px solid #e4e7eb;
}
.inv-furniture-name {
  font-weight: 700;
  font-size: 14px;
  color: #80AC03;
}
.inv-furniture-actions {
  display: flex;
  gap: 6px;
}
.inv-desc {
  margin: 10px 14px 0;
  padding: 8px 12px;
  background: #f9fafb;
  border-left: 3px solid #d0d7de;
  border-radius: 4px;
  font-size: 12px;
  color: #4b5563;
  white-space: pre-wrap;
  word-break: break-word;
  font-family: inherit;
}

/* ── Image Sections ── */
.inv-img-section {
  display: flex;
  gap: 16px;
  padding: 12px 14px;
  flex-wrap: wrap;
}
.inv-img-group {
  flex: 1;
  min-width: 180px;
}
.inv-img-group-defect {
  border-left: 1px solid rgba(234,84,85,0.3);
  padding-left: 16px;
}
.inv-img-label {
  font-size: 11px;
  font-weight: 700;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
}
.inv-img-label-defect { color: #ea5455; }
.inv-img-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.inv-thumb {
  width: 80px;
  height: 70px;
  object-fit: cover;
  border-radius: 5px;
  border: 1px solid #e4e7eb;
  cursor: pointer;
  transition: opacity 0.15s;
}
.inv-thumb:hover { opacity: 0.85; }

/* ── Form Panel ── */
.inv-form-panel {
  margin: 0 16px 20px;
  border: 1px solid #d0d7de;
  border-radius: 8px;
  overflow: hidden;
}
.inv-form-header {
  background: #f5f7fa;
  border-bottom: 1px solid #d0d7de;
  padding: 10px 16px;
}
.inv-form-title {
  font-weight: 700;
  font-size: 14px;
  color: #80AC03;
}
.inv-form { padding: 16px; }
.inv-field {
  margin-bottom: 14px;
}
.inv-field-row {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}
.inv-field-row .inv-field { flex: 1; min-width: 220px; }
.inv-label {
  display: block;
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 5px;
}
.inv-label-defect { color: #ea5455; }
.inv-required { color: #ea5455; }
.inv-input {
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d0d7de;
  border-radius: 6px;
  font-size: 13px;
  color: #5e5873;
  outline: none;
  transition: border-color 0.15s;
  box-sizing: border-box;
  font-family: inherit;
}
.inv-input:focus { border-color: #80AC03; box-shadow: 0 0 0 2px rgba(128,172,3,0.15); }
.inv-textarea { resize: vertical; min-height: 70px; }

/* Upload area */
.inv-upload-area {
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px dashed #d0d7de;
  border-radius: 6px;
  padding: 12px;
  cursor: pointer;
  font-size: 12px;
  color: #6b7280;
  text-align: center;
  transition: border-color 0.15s, background 0.15s;
  margin-bottom: 8px;
}
.inv-upload-area:hover { border-color: #80AC03; background: rgba(128,172,3,0.06); color: #80AC03; }
.inv-upload-area-defect:hover { border-color: #ea5455; background: rgba(234,84,85,0.05); color: #ea5455; }

/* Image previews */
.inv-preview-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.inv-preview-wrap {
  position: relative;
}
.inv-preview-img {
  width: 80px;
  height: 70px;
  object-fit: cover;
  border-radius: 5px;
  border: 1px solid #d0d7de;
  cursor: pointer;
  transition: opacity 0.15s;
}
.inv-preview-img:hover { opacity: 0.85; }
.inv-remove-img {
  position: absolute;
  top: -5px;
  right: -5px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #ea5455;
  color: #fff;
  border: none;
  cursor: pointer;
  font-size: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  padding: 0;
}

/* Form actions */
.inv-form-actions {
  display: flex;
  gap: 8px;
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid #e4e7eb;
}

/* ── Preview button ── */
.inv-btn-preview {
  background: rgba(255,255,255,0.15);
  color: #fff;
  border: 1px solid rgba(255,255,255,0.4);
}
.inv-btn-preview:hover { background: rgba(255,255,255,0.25); opacity: 1; }

/* ── Preview Overlay ── */
.preview-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.6);
  z-index: 9999;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding: 24px 16px;
  overflow-y: auto;
}
.preview-modal {
  background: #f8f8f8;
  border-radius: 10px;
  width: 100%;
  max-width: 860px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.35);
  display: flex;
  flex-direction: column;
}
.preview-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px 0 0;
  background: #80AC03;
  border-radius: 10px 10px 0 0;
  flex-shrink: 0;
  gap: 10px;
}
.preview-tabs {
  display: flex;
  flex: 1;
}
.preview-tab {
  background: transparent;
  border: none;
  color: rgba(255,255,255,0.65);
  font-size: 13px;
  font-weight: 600;
  padding: 14px 18px;
  cursor: pointer;
  border-bottom: 3px solid transparent;
  transition: all 0.15s;
  white-space: nowrap;
}
.preview-tab:hover { color: #fff; background: rgba(255,255,255,0.08); }
.preview-tab-active { color: #fff; border-bottom-color: #fff; background: rgba(255,255,255,0.1); }
.preview-close-btn {
  background: rgba(255,255,255,0.15);
  border: none;
  color: #fff;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  font-size: 18px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}
.preview-close-btn:hover { background: rgba(255,255,255,0.3); }
.preview-modal-body {
  overflow-y: auto;
  padding: 20px;
  max-height: calc(100vh - 100px);
}

/* ── Image Lightbox ── */
.img-lightbox-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,0.85);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
}
.img-lightbox-img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 6px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.5);
}
.img-lightbox-close {
  position: absolute;
  top: 20px;
  right: 24px;
  background: rgba(255,255,255,0.15);
  border: none;
  color: #fff;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  font-size: 22px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}
.img-lightbox-close:hover { background: rgba(255,255,255,0.3); }

/* ── Preview Paper (white A4-like sheet) ── */
.prev-paper {
  background: #fff;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  font-size: 12px;
  color: #212529;
  padding: 28px 32px;
  border-radius: 6px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.1);
  line-height: 1.5;
}
.prev-paper-block {
  padding: 8px;
  margin-bottom: 8px;
  border-radius: 4px;
}

/* Key Handover preview */
.prev-kh-title {
  text-align: center;
  font-size: 16px;
  font-weight: bold;
  color: #212529;
  margin: 0 0 16px;
}
.prev-kh-section {
  font-size: 13px;
  font-weight: bold;
  color: #212529;
  margin: 14px 0 6px;
}
.prev-kh-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  color: #212529;
  margin-bottom: 4px;
}
.prev-kh-table th,
.prev-kh-table td {
  border: 1px solid #dee2e6;
  padding: 6px 8px;
}
.prev-kh-table thead th {
  background: #f8f9fa;
  font-weight: 600;
  text-align: center;
}
.prev-kh-sigs {
  display: flex;
  justify-content: space-between;
  margin-top: 32px;
  gap: 40px;
}
.prev-kh-sig-box {
  flex: 1;
  font-size: 12px;
  color: #212529;
}

/* Header */
.prev-header-banner {
  background: #80AC03;
  color: #fff;
  padding: 14px 16px 10px;
  margin-bottom: 14px;
}
.prev-header-title {
  font-size: 17px;
  font-weight: bold;
  letter-spacing: 1.5px;
}
.prev-header-sub {
  font-size: 10px;
  color: rgba(255,255,255,0.7);
  margin-top: 3px;
}

/* Info cards */
.prev-info-grid {
  display: flex;
  gap: 10px;
  margin-bottom: 14px;
}
.prev-info-card {
  flex: 1;
  background: #f5f7fa;
  border: 1px solid #d0d7de;
  border-radius: 4px;
  padding: 8px 12px;
}
.prev-info-row {
  display: flex;
  padding: 3px 0;
  border-bottom: 1px solid #e4e7eb;
  font-size: 10px;
}
.prev-info-row:last-child { border-bottom: none; }
.prev-info-label { color: #6b7280; width: 90px; flex-shrink: 0; }
.prev-info-val   { color: #5e5873; font-weight: 600; }

/* Section titles */
.prev-section-title {
  background: #80AC03;
  color: #fff;
  font-size: 11px;
  font-weight: bold;
  letter-spacing: 0.5px;
  padding: 5px 10px;
  margin-top: 14px;
}
.prev-room-block { margin-top: 16px; }
.prev-room-title {
  background: rgba(128,172,3,0.08);
  border-left: 4px solid #80AC03;
  padding: 5px 10px;
  font-size: 11px;
  font-weight: bold;
  color: #80AC03;
}
.prev-room-label { color: #6b7280; font-weight: normal; }
.prev-defect-title {
  background: rgba(234,84,85,0.06);
  border-left: 4px solid #ea5455;
  padding: 5px 10px;
  font-size: 11px;
  font-weight: bold;
  color: #ea5455;
  margin-top: 8px;
}

/* Tables */
.prev-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 10px;
}
.prev-th {
  background: #80AC03;
  color: #fff;
  padding: 6px;
  border: 1px solid #6259d6;
  font-weight: 700;
  text-align: left;
}
.prev-th-defect {
  background: #ea5455;
  border-color: #d13c3d;
}
.prev-td {
  border: 1px solid #d0d7de;
  padding: 6px;
  vertical-align: middle;
}
.prev-td-name   { font-weight: 600; color: #80AC03; }
.prev-td-desc   { color: #4b5563; white-space: pre-wrap; word-break: break-word; vertical-align: top; }
.prev-td-center { text-align: center; }
.prev-row-stripe        { background: #f8f8f8; }
.prev-row-stripe-defect { background: rgba(234,84,85,0.04); }
.prev-placeholder { color: #9ca3af; font-size: 10px; font-style: italic; }

/* Images */
.prev-img-grid { display: flex; flex-wrap: wrap; gap: 4px; }
.prev-thumb {
  width: 70px;
  height: 58px;
  object-fit: cover;
  border-radius: 3px;
  border: 1px solid #e4e7eb;
}
.prev-thumb-defect { border-color: rgba(234,84,85,0.4); }
.prev-no-img { color: #9ca3af; font-size: 10px; }

/* Note */
.prev-note-box {
  background: rgba(255,159,67,0.08);
  border-left: 4px solid #ff9f43;
  padding: 6px 10px;
  margin-top: 6px;
  font-size: 10px;
  color: #5e5873;
}

/* Disclaimer */
.prev-disclaimer {
  background: #f5f7fa;
  border: 1px solid #d0d7de;
  border-left: 4px solid #80AC03;
  padding: 10px 12px;
  margin-top: 20px;
  font-size: 10px;
  color: #374151;
  line-height: 1.6;
}
.prev-disclaimer p { margin: 0 0 5px; }
.prev-disclaimer p:last-child { margin-bottom: 0; }

/* Signatures */
.prev-sig-section { display: flex; gap: 20px; margin-top: 24px; }
.prev-sig-box {
  flex: 1;
  border: 1px solid #d0d7de;
  border-top: 3px solid #80AC03;
  padding: 12px;
  background: #f9fafb;
}
.prev-sig-role { font-size: 12px; font-weight: bold; color: #80AC03; letter-spacing: 1px; }
.prev-sig-name { font-size: 10px; color: #6b7280; margin: 2px 0 10px; }
.prev-sig-field { display: flex; align-items: center; gap: 8px; margin-top: 8px; }
.prev-sig-label { font-size: 9px; color: #6b7280; width: 55px; flex-shrink: 0; }
.prev-sig-line  { flex: 1; border-bottom: 1px solid #9ca3af; }

/* Misc */
.prev-empty { color: #9ca3af; font-style: italic; font-size: 11px; padding: 10px 6px; }
.prev-empty-full { text-align: center; padding: 40px; }

/* ── Mobile ── */
@media (max-width: 576px) {

  /* Wrapper */
  .inv-wrapper {
    padding: 12px 10px;
  }

  /* Top bar — title full width, buttons scroll horizontally */
  .inv-topbar {
    flex-direction: column;
    align-items: flex-start;
    padding: 12px 14px;
    gap: 10px;
    border-radius: 8px;
  }
  .inv-topbar-title {
    font-size: 15px;
  }
  .inv-topbar-actions {
    width: 100%;
    overflow-x: auto;
    flex-wrap: nowrap;
    padding-bottom: 4px;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
  }
  .inv-topbar-actions::-webkit-scrollbar { display: none; }
  .inv-btn {
    padding: 7px 11px;
    font-size: 12px;
    flex-shrink: 0;
  }

  /* Tabs — horizontal scroll, no wrap */
  .inv-tabs-bar {
    flex-wrap: nowrap;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    padding-bottom: 0;
    margin-bottom: 12px;
  }
  .inv-tabs-bar::-webkit-scrollbar { display: none; }
  .inv-tab {
    padding: 8px 12px;
    font-size: 12px;
    white-space: nowrap;
    flex-shrink: 0;
  }

  /* Room panel */
  .inv-panel {
    border-radius: 8px;
  }

  /* Room header — stack title + actions */
  .inv-room-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    padding: 12px 14px;
  }
  .inv-room-title {
    font-size: 15px;
  }
  .inv-room-input {
    min-width: 0;
    width: 100%;
  }
  .inv-room-title-wrap {
    width: 100%;
  }

  /* Toggle */
  .inv-toggle {
    padding: 8px 14px;
    font-size: 12px;
  }

  /* Furniture list */
  .inv-furniture-list {
    padding: 0 10px 12px;
    gap: 10px;
  }

  /* Furniture card */
  .inv-furniture-top {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    padding: 10px 12px;
  }
  .inv-furniture-name {
    font-size: 13px;
  }
  .inv-furniture-actions {
    width: 100%;
    justify-content: flex-end;
  }

  /* Images — stack furniture + defect vertically */
  .inv-img-section {
    flex-direction: column;
    gap: 12px;
    padding: 10px 12px;
  }
  .inv-img-group-defect {
    border-left: none;
    border-top: 1px solid rgba(234,84,85,0.25);
    padding-left: 0;
    padding-top: 12px;
  }
  .inv-img-group {
    min-width: 0;
  }
  .inv-thumb {
    width: 70px;
    height: 60px;
  }

  /* Form panel */
  .inv-form-panel {
    margin: 0 10px 16px;
  }
  .inv-form {
    padding: 12px;
  }
  /* Force single-column field rows */
  .inv-field-row {
    flex-direction: column;
    gap: 0;
  }
  .inv-field-row .inv-field {
    min-width: 0;
  }

  /* Form actions */
  .inv-form-actions {
    flex-wrap: wrap;
  }
  .inv-form-actions .inv-btn {
    flex: 1;
    justify-content: center;
  }

  /* Image upload previews */
  .inv-preview-img {
    width: 70px;
    height: 60px;
  }

  /* Preview modal — full screen on mobile */
  .preview-overlay {
    padding: 0;
    align-items: flex-end;
  }
  .preview-modal {
    max-width: 100%;
    border-radius: 14px 14px 0 0;
    max-height: 92vh;
  }
  .preview-modal-body {
    max-height: calc(92vh - 54px);
    padding: 14px;
  }
  .preview-modal-header {
    border-radius: 14px 14px 0 0;
    padding: 14px 16px;
  }

  /* Preview doc — smaller text and padding on mobile */
  .preview-doc {
    padding: 14px 12px;
    font-size: 10px;
  }
  .prev-header-title { font-size: 14px; }
  .prev-info-grid { flex-direction: column; }
  .prev-sig-section { flex-direction: column; gap: 12px; }
}
</style>